import React from 'react';
import { Facebook, Twitter, Instagram, Linkedin, Youtube, X } from 'lucide-react';

// Custom TikTok Icon Component (shared by Footer and the social modal)
export const TikTokIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

export const SocialModal = ({ show, isOpening, isClosing, onClose, socialLinks }) => {
  if (!show) return null;
  return (
    <div
      data-testid="footer-social-modal"
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${
        isClosing ? 'opacity-0' : isOpening ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative transform transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : isOpening ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800 mb-2">
            Let's Get Social!
          </h2>
          <p className="text-gray-600">Follow TrainerList on social media</p>
        </div>

        {/* Social Links */}
        <div className="space-y-4">
          {socialLinks.facebook && (
            <a
              href={socialLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 transition-all group"
            >
              <div className="rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0" style={{ width: '48px', height: '48px', minWidth: '48px', minHeight: '48px' }}>
                <Facebook className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 group-hover:text-blue-600">Facebook</p>
                <p className="text-sm text-gray-600 truncate">{socialLinks.facebook}</p>
              </div>
            </a>
          )}

          {socialLinks.twitter && (
            <a
              href={socialLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-sky-50 to-sky-100 hover:from-sky-100 hover:to-sky-200 transition-all group"
            >
              <div className="rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0" style={{ width: '48px', height: '48px', minWidth: '48px', minHeight: '48px' }}>
                <Twitter className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 group-hover:text-sky-600">Twitter</p>
                <p className="text-sm text-gray-600 truncate">{socialLinks.twitter}</p>
              </div>
            </a>
          )}

          {socialLinks.instagram && (
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-red-50 to-gray-200 hover:from-red-100 hover:to-gray-300 transition-all group"
            >
              <div className="rounded-full bg-gradient-to-br from-red-600 to-gray-800 flex items-center justify-center flex-shrink-0" style={{ width: '48px', height: '48px', minWidth: '48px', minHeight: '48px' }}>
                <Instagram className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 group-hover:text-red-600">Instagram</p>
                <p className="text-sm text-gray-600 truncate">{socialLinks.instagram}</p>
              </div>
            </a>
          )}

          {socialLinks.linkedin && (
            <a
              href={socialLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-100 hover:from-blue-100 hover:to-indigo-200 transition-all group"
            >
              <div className="rounded-full bg-blue-700 flex items-center justify-center flex-shrink-0" style={{ width: '48px', height: '48px', minWidth: '48px', minHeight: '48px' }}>
                <Linkedin className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 group-hover:text-blue-700">LinkedIn</p>
                <p className="text-sm text-gray-600 truncate">{socialLinks.linkedin}</p>
              </div>
            </a>
          )}

          {socialLinks.youtube && (
            <a
              href={socialLinks.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 transition-all group"
            >
              <div className="rounded-full bg-red-600 flex items-center justify-center flex-shrink-0" style={{ width: '48px', height: '48px', minWidth: '48px', minHeight: '48px' }}>
                <Youtube className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 group-hover:text-red-600">YouTube</p>
                <p className="text-sm text-gray-600 truncate">{socialLinks.youtube}</p>
              </div>
            </a>
          )}

          {socialLinks.tiktok && (
            <a
              href={socialLinks.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-all group"
            >
              <div className="rounded-full bg-black flex items-center justify-center flex-shrink-0" style={{ width: '48px', height: '48px', minWidth: '48px', minHeight: '48px' }}>
                <TikTokIcon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 group-hover:text-gray-900">TikTok</p>
                <p className="text-sm text-gray-600 truncate">{socialLinks.tiktok}</p>
              </div>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export const CollaborationModal = ({ show, isOpening, isClosing, onClose }) => {
  if (!show) return null;
  return (
    <div
      data-testid="footer-collaboration-modal"
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto transition-opacity duration-300 ${
        isClosing ? 'opacity-0' : isOpening ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8 relative transform transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : isOpening ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-10"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-800 text-white p-8 rounded-t-2xl">
          <h2 className="text-3xl font-bold mb-2">
            Partner With <span className="font-black">TrainerList</span>
          </h2>
          <p className="text-lg opacity-90">Let's Grow Together</p>
        </div>

        {/* Content */}
        <div className="p-8 max-h-[60vh] overflow-y-auto">
          <p className="text-gray-700 mb-4 leading-relaxed">
            <span className="font-bold text-gray-900">TrainerList</span> is Canada's newest and most dynamic platform connecting personal trainers with individuals seeking high-quality, reliable fitness services — both online and in person.
          </p>

          <p className="text-gray-700 mb-4 leading-relaxed">
            We are currently seeking <span className="font-semibold text-red-700">sponsorship and collaboration partners</span> who share our passion for health, wellness, and innovation in the fitness industry. This is a unique opportunity to gain exposure within a growing, highly engaged community of trainers, trainees, and fitness enthusiasts.
          </p>

          <div className="bg-gradient-to-r from-red-50 to-gray-200 p-6 rounded-xl mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              By partnering with <span className="font-black text-red-700">TrainerList</span>, your brand will benefit from:
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span className="text-gray-700">Direct visibility across our platform and social channels</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span className="text-gray-700">Co-branded marketing opportunities</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span className="text-gray-700">Presence at fitness events and local activations</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span className="text-gray-700">Product placement, giveaways, and user engagement campaigns</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span className="text-gray-700">The chance to align with a mission-driven brand that supports personal development and healthy lifestyles</span>
              </li>
            </ul>
          </div>

          <p className="text-gray-700 mb-6 leading-relaxed">
            Whether you're a <span className="font-semibold">health brand</span>, <span className="font-semibold">fitness apparel company</span>, <span className="font-semibold">tech solution</span>, or <span className="font-semibold">local business</span>, we'd love to explore how we can work together.
          </p>

          <div className="bg-gradient-to-r from-red-600 to-red-800 text-white p-6 rounded-xl text-center">
            <p className="text-2xl font-bold mb-4">Let's build something impactful.</p>
            <a
              href="/contact"
              onClick={(e) => {
                e.preventDefault();
                onClose();
                setTimeout(() => {
                  window.location.href = '/contact';
                }, 300);
              }}
              className="inline-block px-8 py-3 bg-white text-red-700 font-semibold rounded-lg hover:shadow-lg transition-all"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const HowItWorksModal = ({ show, isOpening, isClosing, onClose }) => {
  if (!show) return null;
  const steps = [
    { n: 1, title: 'Search for Trainers', desc: 'Browse our extensive list of certified personal trainers in your area' },
    { n: 2, title: 'Review Profiles', desc: 'Check trainer specializations, ratings, and availability' },
    { n: 3, title: 'Book a Session', desc: 'Schedule your training session online or in-person' },
    { n: 4, title: 'Start Your Fitness Journey', desc: 'Begin training with your chosen professional trainer' },
  ];
  return (
    <div
      data-testid="footer-how-it-works-modal"
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${
        isClosing ? 'opacity-0' : isOpening ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl max-w-2xl w-full relative transform transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : isOpening ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-10"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-800 text-white p-5 rounded-t-2xl">
          <h2 className="text-2xl font-bold text-center">How It Works</h2>
          <p className="text-center text-sm opacity-90 mt-1">Your journey to finding the perfect trainer</p>
        </div>

        {/* Content with Image */}
        <div className="p-5">
          <img
            src="https://images.unsplash.com/photo-1531403009284-440f080d1e12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxwcm9jZXNzJTIwZmxvd3xlbnwwfHx8fDE3NjE5NTIzOTB8MA&ixlib=rb-4.1.0&q=85"
            alt="How It Works - Platform Workflow"
            className="w-full h-auto rounded-lg shadow-lg"
          />

          {/* Step-by-step explanation */}
          <div className="mt-5 space-y-3">
            {steps.map((step) => (
              <div key={step.n} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center font-bold flex-shrink-0 text-sm">
                  {step.n}
                </div>
                <div>
                  <h3 className="font-bold text-base text-gray-900">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
